import { matrixToPermissionStrings, type PersonaId, type PermissionResource } from '@evi/shared';
import type { NavItem, NavLeaf, NavParent, NavSection } from './nav';
import { regroupForSuperAdmin } from './superAdminNav';

/**
 * Nav visibility filter — persona- AND capability-aware.
 *
 * Two independent branches keyed off the persona axis:
 *
 *   - LIMITED personas (fleet_admin, sales_agent, building_admin) get
 *     RESTRICTIVE-LIST treatment. They see ONLY items whose
 *     `visibleFor` explicitly includes their PersonaId. This branch is
 *     UNCHANGED by the capability work — curated personas have a
 *     hand-authored nav (e.g. sales_agent = Dashboard, My Chargers,
 *     Reports, My Profile) and do NOT consult live permissions.
 *
 *   - DEFAULT-TIER personas (super_admin / tenant_admin / operator /
 *     viewer / driver — anyone NOT in LIMITED_PERSONAS) are now
 *     CAPABILITY-DRIVEN. An item shows only if the user holds its
 *     mapped permission bit (DEFAULT_TIER_RULES below), OR the item is
 *     tier-gated and the persona is in its allow-list, OR it is the
 *     always-on Dashboard. This REPLACES the previous allow-by-default
 *     behaviour, where default-tier admins saw every untagged item.
 *
 * Composition with `visibleFor` (default tier only):
 *   `visibleFor` remains a HARD PRE-FILTER that can only NARROW, never
 *   widen. An item tagged visibleFor:['super_admin'] (e.g. agents) is
 *   hidden from tenant_admin even though the tier rule below lists
 *   super_admin+tenant_admin — the tag wins. This keeps nav in lockstep
 *   with server routes that 403 the narrower set, so we never render a
 *   nav item that dead-ends in a 403.
 *
 * super_admin bypass:
 *   super_admin sees everything that survives the visibleFor pre-filter,
 *   skipping the permission gate entirely — mirroring
 *   requireMatrixPermission's super_admin bypass (authz.ts). Necessary
 *   because super_admin's DERIVED permission set is incomplete: it is
 *   seeded from the legacy RolePermissions string list, which predates
 *   the Coupon / Gamification / Dashboard / Fleet matrix resources, so
 *   super_admin's resolved strings lack e.g. 'coupon:read'. Permission-
 *   gating it would wrongly hide those items.
 *
 *   - hiddenFrom is checked first and always wins.
 *   - persona === null (no authenticated user) hides everything (fail-
 *     closed).
 *
 * Charger alerts (Warren, 4 Nov 2026: "CHARGER ALERTS GO TO EVI ONLY"; ruling
 * R6): the Alerts leaf follows the Api's own flag, can_see_charger_alerts on
 * /auth/me, for every persona, on top of everything above. Not a persona check:
 * a customer's tenant admin and an EVI operator resolve the same persona, and
 * only the Api knows which is which. Absent reads as false, so the leaf hides.
 */

/**
 * Which AUTHORED MENU a persona gets, if any.
 *
 * Replaces the old `LIMITED_PERSONAS: PersonaId[]`. The list said WHICH
 * personas were curated; it could not say WHAT they were curated into,
 * so every persona needed its own `visibleFor` tags scattered through
 * nav.ts and three templates that should share one menu got three
 * different accidental ones.
 *
 * A persona absent from this map takes the DEFAULT TIER: its menu is
 * computed from its live permission bits via DEFAULT_TIER_RULES below.
 * That path is already template-driven and stays as it is.
 *
 * ── WHY building_admin WAS DEAD CODE ────────────────────────────────
 * The old list named 'building_admin', but no code path produced that
 * PersonaId: TopNav and MobileNavOverlay called `resolvePersonaId`
 * without `template`, so a Site persona resolved to the session tier
 * 'third_party_admin' — a real member of UserRoleSchema, absent from
 * the list and from every visibleFor tag. All three Site templates
 * therefore fell through to the capability branch and got menus of 12,
 * 9 and 4 items driven purely by their seeded matrices, while the one
 * leaf authored for them could never render. Passing `template` is the
 * other half of this change; neither half works alone.
 *
 * ── ADDING A PERSONA ────────────────────────────────────────────────
 * A new Site-shaped template is one row here plus its Role template.
 * Authoring a NEW menu is one row here plus `visibleFor` tags on its
 * leaves — no change to the filter itself.
 */
export type MenuId = 'site' | 'fleet' | 'partner' | 'partner_team' | 'sales';

const PERSONA_MENU: Partial<Record<PersonaId, MenuId>> = {
  // All three Site templates share ONE authored menu. This is the row
  // that makes a fourth Site-shaped template a data change.
  building_admin: 'site',
  charger_owner: 'site',
  property_manager: 'site',
  fleet_admin: 'fleet',
  sales_agent: 'partner',
  // A team member under a Sales Partner (ruling 7): Jobs only.
  sales_partner_member: 'partner_team',
  sales_rep: 'sales',
  sales_manager: 'sales',
};

/** The authored menu for this persona, or null for the default tier. */
export function menuForPersona(persona: PersonaId): MenuId | null {
  return PERSONA_MENU[persona] ?? null;
}

function hasAuthoredMenu(persona: PersonaId): boolean {
  return menuForPersona(persona) !== null;
}

/**
 * Capability rule for a DEFAULT-TIER nav leaf, keyed by NavLeaf.id.
 *
 *   - 'always'      — shown to every default-tier persona (Dashboard).
 *   - 'permission'  — shown iff the user's live permission set
 *                     (PublicUser.permissions, recomputed from the
 *                     current Role matrix on /auth/me — see c4898b0)
 *                     contains the resource's read string. The string
 *                     is derived through matrixToPermissionStrings so
 *                     the irregular mappings (Reports→'report:read',
 *                     Log→'audit:read') stay correct without hardcoding.
 *   - 'tier'        — shown iff the persona is in the allow-list. Used
 *                     for surfaces gated by system-role tier rather than
 *                     a matrix bit: platform/admin-config pages, and
 *                     Fleets (fleet access is role + AccessAssignment
 *                     gated, never a matrix string — 'fleet:read' is not
 *                     producible by matrixToPermissionStrings, so a
 *                     permission rule would be unsatisfiable).
 *
 * Every default-tier leaf that can survive the visibleFor pre-filter
 * MUST have an entry. A leaf with no entry fails closed (hidden). The
 * SP/building_admin/fleet_admin leaves (spMyChargers, spReports,
 * buildingAdminReports, spProfile, fleetAdminDashboard, fleetAdminReports) are
 * intentionally absent: their visibleFor tags exclude every default-
 * tier persona, so they never reach this lookup.
 */
type NavCapabilityRule =
  | { kind: 'always' }
  | { kind: 'permission'; resource: PermissionResource }
  | { kind: 'tier'; personas: ReadonlyArray<PersonaId> }
  /** Shown iff the user holds a permission string with this prefix (User.permissions on /auth/me), for every
   *  default-tier persona, super_admin included: the page is the holder's, and the Api checks the same strings. */
  | { kind: 'held'; prefix: string };

const DEFAULT_TIER_RULES: Record<string, NavCapabilityRule> = {
  salesStaff: { kind: 'tier', personas: ['super_admin'] },
  salesStatements: { kind: 'tier', personas: ['super_admin'] },
  // Overview
  dashboard: { kind: 'always' },
  locations: { kind: 'permission', resource: 'Location' },
  chargers: { kind: 'permission', resource: 'Charger' },
  sessions: { kind: 'permission', resource: 'Session' },
  // Alerts — gated on the resource added 2026-09-24. Before it, this
  // surface had no permission an operator could hold and no leaf.
  alerts: { kind: 'permission', resource: 'Alert' },
  reports: { kind: 'permission', resource: 'Reports' },
  // Manage › Users
  team: { kind: 'permission', resource: 'User' },
  drivers: { kind: 'permission', resource: 'User' },
  lockedOut: { kind: 'permission', resource: 'User' },
  groups: { kind: 'permission', resource: 'Group' },
  // Manage › Billing
  // Money — one leaf, four tabs (ruling 3). Same Billing bit the four
  // old leaves carried, so who sees it is unchanged.
  money: { kind: 'permission', resource: 'Billing' },
  // Approval cards (Warren, 29 October 2026): the holders of an approver type, approve:driver_money and the rest.
  approvals: { kind: 'held', prefix: 'approve:' },
  // Manage › RFID
  rfidCards: { kind: 'permission', resource: 'RFID' },
  rfidAssignments: { kind: 'permission', resource: 'RFID' },
  // Manage › leaves
  tariffs: { kind: 'permission', resource: 'Tariff' },
  coupons: { kind: 'permission', resource: 'Coupon' },
  // Reads are scoped server-side to assigned/submitted deployments; showing
  // the entry grants no commissioning, credential or launch permission.
  deployments: { kind: 'always' },
  procurement: { kind: 'tier', personas: ['super_admin', 'operator'] },
  factorySettings: { kind: 'tier', personas: ['super_admin'] },
  // Submit a Location for staff: POST /network-submissions only needs a
  // signed-in caller, and the order detail and ops email point staff here.
  // viewer is read-only, so it is left out (nav.ts visibleFor narrows too).
  submitLocation: { kind: 'tier', personas: ['super_admin', 'tenant_admin', 'operator'] },
  // Announcements — a GRANTED BUT UNREACHABLE capability until now. The
  // seed gives both operator and tenant_admin announcement:read/write,
  // the routes accept it, and the leaf existed — but an unmapped leaf
  // fails closed in this table, so the only persona that ever saw it
  // was super_admin, through the bypass. Mapping it to its own resource
  // lights it for exactly the personas the seed already trusted with it.
  announcements: { kind: 'permission', resource: 'Announcement' },
  // Fleet access is tier-gated server-side (resolveAssignedFleetIds →
  // 'all' for super_admin/tenant_admin, 'none' for operator/viewer).
  // No matrix string exists for it. See the comment above.
  // Phase 1a — fleet nav is now capability-gated on Fleet:read (Fleet
  // landed in RESOURCE_MAP, so the bit is finally satisfiable). super_admin
  // bypasses; tenant_admin holds Fleet:read via its seed extras; operator/
  // viewer lack the bit (unchanged — they didn't see fleets under the prior
  // super+tenant tier gate either). fleet_admin is a LIMITED persona and
  // sees Fleets via the visibleFor restrictive path, not this default-tier
  // rule, so this rule doesn't affect it.
  fleets: { kind: 'permission', resource: 'Fleet' },
  // Store orders — Billing-gated, mirroring the server's
  // requireMatrixPermission('Billing') gate on /admin/orders.
  orders: { kind: 'permission', resource: 'Billing' },
  // Gamification — every view gates on the single Gamification:read bit.
  leaderboard: { kind: 'permission', resource: 'Gamification' },
  badges: { kind: 'permission', resource: 'Gamification' },
  earningRules: { kind: 'permission', resource: 'Gamification' },
  friendships: { kind: 'permission', resource: 'Gamification' },
  streaks: { kind: 'permission', resource: 'Gamification' },
  challenges: { kind: 'permission', resource: 'Gamification' },
  unlocks: { kind: 'permission', resource: 'Gamification' },
  // Anti-abuse is a security surface folded under the Gamification gate
  // server-side, but the override spec gates it by tier (admin-only).
  antiAbuse: { kind: 'tier', personas: ['super_admin', 'tenant_admin'] },
  // System
  // Roles & Permissions follows the Role bit now, not a hardcoded tier.
  // Ruling 1 (2026-09-24) gave tenant_admin Role read+write — "a tenant
  // admin does that plus configuration, money terms and the team" — so
  // a super_admin-only tier rule would have granted the capability and
  // hidden the page that uses it. operator is deliberately excluded and
  // still is: it holds no Role bit.
  roles: { kind: 'permission', resource: 'Role' },
  access: { kind: 'tier', personas: ['super_admin', 'tenant_admin'] },
  audit: { kind: 'permission', resource: 'Log' },
  // agents / agentOpsQueue carry visibleFor:['super_admin'] in nav.ts,
  // which narrows the tier list below to super_admin only via the
  // pre-filter (composition). The tenant_admin entry is the declared
  // ceiling; the tag is the effective floor.
  agents: { kind: 'tier', personas: ['super_admin', 'tenant_admin'] },
  agentOpsQueue: { kind: 'tier', personas: ['super_admin', 'tenant_admin'] },
  settings: { kind: 'tier', personas: ['super_admin', 'tenant_admin'] },
  // RFID registration pricing — super_admin only. The nav.ts visibleFor
  // already narrows it; this keeps the default-tier lookup non-fail-closed.
  rfidPricing: { kind: 'tier', personas: ['super_admin'] },
  // Site Electricity (Site Host Terms): super_admin only, same shape.
  siteElectricity: { kind: 'tier', personas: ['super_admin'] },
  // Autocharge settings + kill switch — super_admin only, same shape.
  autocharge: { kind: 'tier', personas: ['super_admin'] },
  // Tenant roster + per-tenant billing — super_admin only, same shape as
  // rfidPricing. visibleFor narrows it; the super_admin bypass admits it.
  tenants: { kind: 'tier', personas: ['super_admin'] },
};

/**
 * Derive the read permission string for a resource via the canonical
 * matrix translator (single read-only synthetic row). Returns null only
 * for resources the translator can't represent — none of the resources
 * referenced in DEFAULT_TIER_RULES fall into that bucket (all are in
 * RESOURCE_MAP with hasRead:true).
 */
function readPermissionString(resource: PermissionResource): string | null {
  const out = matrixToPermissionStrings([
    { resource, create: false, read: true, update: false, delete: false },
  ]);
  return out[0] ?? null;
}

/** Leaves only for EVI staff, whatever the persona (see the header). */
const EVI_STAFF_LEAVES: ReadonlySet<string> = new Set(['alerts']);

export function isVisibleToPersona(
  item: NavLeaf | NavParent,
  persona: PersonaId | null,
  permissions: ReadonlyArray<string>,
): boolean {
  if (persona === null) return false;
  if (item.hiddenFrom?.includes(persona)) return false;

  if (hasAuthoredMenu(persona)) {
    // Curated restrictive allow-list — UNCHANGED. Only show if
    // visibleFor explicitly includes this persona. Does not consult
    // permissions: curated personas have a hand-authored nav.
    return item.visibleFor?.includes(persona) ?? false;
  }

  // ── Default tier ────────────────────────────────────────────────
  // visibleFor is a hard pre-filter that can only narrow (composition).
  if (item.visibleFor && !item.visibleFor.includes(persona)) return false;

  // A held permission string gates every default-tier persona alike: the Api checks the same string.
  const held = item.kind === 'leaf' ? DEFAULT_TIER_RULES[item.id] : undefined;
  if (held?.kind === 'held') return permissions.some((p) => p.startsWith(held.prefix));

  // super_admin bypass — sees everything that survived the pre-filter.
  if (persona === 'super_admin') return true;

  // Parents carry no capability bit of their own — admit them here and
  // let the surviving-children walk in filterNavItemsForPersona decide.
  if (item.kind === 'parent') return true;

  const rule = DEFAULT_TIER_RULES[item.id];
  if (!rule) return false; // fail-closed: unmapped leaf is hidden.
  if (rule.kind === 'always') return true;
  if (rule.kind === 'tier') return rule.personas.includes(persona);
  if (rule.kind === 'held') return permissions.some((p) => p.startsWith(rule.prefix));
  const required = readPermissionString(rule.resource);
  return required !== null && permissions.includes(required);
}

/**
 * Filter a list of NavItems by persona + live permissions, walking into
 * parents and dropping parents whose children all hid. A parent renders
 * when it survives isVisibleToPersona AND has at least one visible child
 * (a parent with no surviving children is pointless to render).
 *
 * For limited personas, a parent without its own visibleFor flag still
 * renders as long as it has at least one visible child — preserves the
 * prior "parent shows if any child shows" semantic.
 */
export function filterNavItemsForPersona(
  items: ReadonlyArray<NavItem>,
  persona: PersonaId | null,
  permissions: ReadonlyArray<string>,
  assignedDeployment = false,
  canSeeChargerAlerts = false,
): NavItem[] {
  if (persona === null) return [];
  const out: NavItem[] = [];
  const shows = (i: NavLeaf | NavParent) => (canSeeChargerAlerts || !EVI_STAFF_LEAVES.has(i.id)) && isVisibleToPersona(i, persona, permissions);
  for (const item of items) {
    if (item.id === 'deployments' && menuForPersona(persona) === 'site' && !assignedDeployment) continue;
    if (item.kind === 'leaf') {
      if (shows(item)) out.push(item);
      continue;
    }
    // Parent
    const visibleChildren = item.children.filter(shows);
    const parentSelfVisible = isVisibleToPersona(item, persona, permissions);
    // An empty group never shows (Warren, 28 October 2026), not even one
    // tagged for an authored menu: a header that opens onto nothing.
    const shouldShow = visibleChildren.length > 0 && (hasAuthoredMenu(persona) || parentSelfVisible);
    if (shouldShow) {
      out.push({ ...item, children: visibleChildren });
    }
  }
  return out;
}

/**
 * Convenience for the desktop / mobile nav: filter every section's
 * items and drop sections that empty out. The 'recents' section is
 * dropped here because its items come from localStorage at render
 * time and aren't part of the persona-filtered set.
 *
 * The Super Admin's admitted leaves are then regrouped into Warren's
 * sections (21 October 2026, item M; config/superAdminNav.ts), with his
 * groups and the EVI shortcuts last (28 October 2026). Every other
 * persona gets admittedNavSections exactly as before.
 */
export function filterNavSectionsForPersona(
  sections: ReadonlyArray<NavSection>,
  persona: PersonaId | null,
  permissions: ReadonlyArray<string>,
  assignedDeployment = false,
  canSeeChargerAlerts = false,
): NavSection[] {
  const admitted = admittedNavSections(sections, persona, permissions, assignedDeployment, canSeeChargerAlerts);
  return persona === 'super_admin' ? regroupForSuperAdmin(admitted) : admitted;
}

/** The sections and leaves the filter admits, in NAV_SECTIONS order: every persona's menu before item M. */
export function admittedNavSections(
  sections: ReadonlyArray<NavSection>,
  persona: PersonaId | null,
  permissions: ReadonlyArray<string>,
  assignedDeployment = false,
  canSeeChargerAlerts = false,
): NavSection[] {
  return sections
    .filter((s) => s.id !== 'recents')
    .map((section): NavSection => ({
      ...section,
      items: filterNavItemsForPersona(section.items, persona, permissions, assignedDeployment, canSeeChargerAlerts),
    }))
    .filter((s) => s.items.length > 0);
}
